'use client';

import { Box, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';

import {
  Button,
  ComponentErrorBoundary,
  LeaderboardFilters,
  LeaderboardTableHeader,
  LeaderboardTableRow,
  LeaderboardTableSkeleton,
} from '@/components';
import { useT } from '@/hooks';
import { LeaderboardTrader, TimeFilter } from '@/types';

interface LeaderboardTableProps {
  categories: string[];
  timePeriods: string[];
  filteredLeaderboard: LeaderboardTrader[];
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  selectedTimeFilter: TimeFilter;
  setSelectedTimeFilter: (val: TimeFilter) => void;
  selectedCategory: string;
  setSelectedCategory: (val: string) => void;
  orderBy: 'PNL' | 'VOL';
  setOrderBy: (val: 'PNL' | 'VOL') => void;
  isLoading?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
}

export const LeaderboardTable = ({
  categories,
  timePeriods,
  filteredLeaderboard,
  searchQuery,
  setSearchQuery,
  selectedTimeFilter,
  setSelectedTimeFilter,
  selectedCategory,
  setSelectedCategory,
  orderBy,
  setOrderBy,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
}: LeaderboardTableProps) => {
  const { t } = useT();

  const renderLeaderboardRow = (entry: LeaderboardTrader, index: number) => (
    <LeaderboardTableRow
      key={entry.id || index}
      entry={entry}
      index={index}
      orderBy={orderBy}
      category={selectedCategory}
    />
  );

  const renderedEntriesList = filteredLeaderboard?.map(renderLeaderboardRow);
  const hasData = filteredLeaderboard?.length > 0;

  const tableHeader = (
    <LeaderboardTableHeader
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      orderBy={orderBy}
      setOrderBy={setOrderBy}
    />
  );

  const filtersPanel = (
    <LeaderboardFilters
      categories={categories}
      timePeriods={timePeriods}
      selectedTimeFilter={selectedTimeFilter}
      setSelectedTimeFilter={setSelectedTimeFilter}
      selectedCategory={selectedCategory}
      setSelectedCategory={setSelectedCategory}
    />
  );

  const title = (
    <Typography
      variant="h4"
      sx={{
        fontSize: { xs: '1.5rem', sm: '2rem' },
        fontWeight: 700,
        mb: 3,
        color: 'text.primary',
      }}
    >
      {t('leaderboard.title')}
    </Typography>
  );

  if (isLoading) {
    return (
      <Box>
        {title}
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Box sx={{ minWidth: { xs: '100%', sm: 600 } }}>
            <Table>
              {tableHeader}
              <TableBody>
                <LeaderboardTableSkeleton rows={10} />
              </TableBody>
            </Table>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <ComponentErrorBoundary componentName="Leaderboard">
      <Box>
        {title}
        {filtersPanel}
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Box sx={{ minWidth: { xs: '100%', sm: 600 } }}>
            <Table>
              {tableHeader}
              <TableBody>
                {hasData ? (
                  renderedEntriesList
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: 'text.secondary' }}>
                        {t('leaderboard.noTradersFound')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>

        {hasData && hasNextPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
            <Button
              variant="outlined"
              onClick={onLoadMore}
              disabled={isFetchingNextPage}
              sx={{
                px: 4,
                py: 1,
                borderRadius: '10px',
                textTransform: 'none',
                fontWeight: 600,
                borderColor: 'divider',
                color: 'text.primary',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'transparent',
                },
              }}
            >
              {isFetchingNextPage ? t('common.loading') : t('portfolio.loadMore')}
            </Button>
          </Box>
        )}
      </Box>
    </ComponentErrorBoundary>
  );
};
